import type { DocumentAnalysisDetails, ModelOptions } from "../types/documentAnalysis";
import type { ProjectDocument } from "../types/documents";

interface ApiResponse<T> {
  ok: boolean;
  error?: string;
  reason?: string;
  document?: ProjectDocument;
  analysis?: DocumentAnalysisDetails;
  models?: string[];
  analysisModel?: string;
  summaryModel?: string;
  documents?: ProjectDocument[];
  text?: string | null;
  status?: string;
}

export async function fetchModelOptions(): Promise<ModelOptions> {
  const response = await fetch("/ai/models");

  if (!response.ok) {
    throw new Error(`Impossible de charger les modèles (statut ${response.status})`);
  }

  const payload = (await response.json()) as ApiResponse<never>;

  if (!payload.ok || !payload.models) {
    throw new Error(payload.error ?? "Réponse invalide du serveur de modèles");
  }

  return {
    models: payload.models,
    analysisModel: payload.analysisModel,
    summaryModel: payload.summaryModel,
  };
}

export async function fetchProjectDocuments(projectId: string): Promise<ProjectDocument[]> {
  const response = await fetch(`/api/projects/${projectId}/documents`);
  const payload: ApiResponse<never> =
    (await safeJson<ApiResponse<never>>(response)) ?? { ok: response.ok, documents: [] };

  if (!response.ok || payload.ok === false || !Array.isArray(payload.documents)) {
    const fallback = payload.error ?? `Échec du chargement des documents (${response.status})`;
    throw new Error(fallback);
  }

  return payload.documents;
}

export async function deleteProjectDocument(projectId: string, documentId: string): Promise<void> {
  const response = await fetch(`/api/projects/${projectId}/documents/${documentId}`, {
    method: "DELETE",
  });

  const payload: ApiResponse<never> = (await safeJson<ApiResponse<never>>(response)) ?? { ok: false };

  if (!response.ok || payload.ok === false) {
    throw new Error(payload.error ?? `Suppression impossible (${response.status})`);
  }
}

export async function fetchDocumentText(documentId: string): Promise<{ text: string; status: string; reason?: string }> {
  const response = await fetch(`/api/documents/${encodeURIComponent(documentId)}/text`);
  const payload = (await response.json()) as ApiResponse<never>;

  const status = payload.status ?? (payload.ok ? "ok" : undefined);
  const text = typeof payload.text === "string" ? payload.text : "";

  if (!response.ok || (!payload.ok && payload.ok !== undefined)) {
    throw new Error(payload.error ?? `Lecture du texte impossible (${response.status})`);
  }

  return { text, status: status ?? "failed", reason: payload.reason };
}

export async function fetchDocumentAnalysis(
  projectId: string,
  documentId: string,
): Promise<{ analysis: DocumentAnalysisDetails; document?: ProjectDocument } | null> {
  const response = await fetch(`/api/projects/${projectId}/documents/${documentId}/analysis`);

  if (response.status === 404) {
    return null;
  }

  const payload = (await response.json()) as ApiResponse<never>;

  if (!response.ok || !payload.ok || !payload.analysis) {
    throw new Error(payload.error ?? `Impossible de récupérer l'analyse (${response.status})`);
  }

  return { analysis: payload.analysis, document: payload.document };
}

export async function startDocumentAnalysis(
  projectId: string,
  documentId: string,
  context?: string,
  model?: string,
): Promise<DocumentAnalysisDetails> {
  const response = await fetch(`/api/projects/${projectId}/documents/${documentId}/analysis`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ context, model }),
  });

  const payload = (await response.json()) as ApiResponse<never>;

  if (!response.ok || !payload.ok || !payload.analysis) {
    throw new Error(payload.error ?? `Analyse impossible (${response.status})`);
  }

  return payload.analysis;
}

export async function updateDocumentContext(
  projectId: string,
  documentId: string,
  context: string,
): Promise<{ analysis: DocumentAnalysisDetails; document?: ProjectDocument }> {
  const response = await fetch(`/api/projects/${projectId}/documents/${documentId}/context`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ context }),
  });

  const payload = (await response.json()) as ApiResponse<never>;

  if (!response.ok || !payload.ok || !payload.analysis) {
    throw new Error(payload.error ?? `Mise à jour du contexte impossible (${response.status})`);
  }

  return { analysis: payload.analysis, document: payload.document };
}

export async function sendAnalysisMessage(
  projectId: string,
  documentId: string,
  message: string,
  model?: string,
): Promise<DocumentAnalysisDetails> {
  const response = await fetch(`/api/projects/${projectId}/documents/${documentId}/chat`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ message, model }),
  });

  const payload = (await response.json()) as ApiResponse<never>;

  if (!response.ok || !payload.ok || !payload.analysis) {
    throw new Error(payload.error ?? `Impossible d'envoyer le message (${response.status})`);
  }

  return payload.analysis;
}

export async function approveDocumentAnalysis(
  projectId: string,
  documentId: string,
): Promise<DocumentAnalysisDetails> {
  const response = await fetch(`/api/projects/${projectId}/documents/${documentId}/analysis/approve`, {
    method: "POST",
  });

  const payload: ApiResponse<never> = (await safeJson<ApiResponse<never>>(response)) ?? { ok: false };

  if (!response.ok || !payload.ok || !payload.analysis) {
    throw new Error(payload.error ?? `Approbation impossible (${response.status})`);
  }

  return payload.analysis;
}

export async function rejectDocumentAnalysis(
  projectId: string,
  documentId: string,
  reason?: string,
): Promise<DocumentAnalysisDetails> {
  const response = await fetch(`/api/projects/${projectId}/documents/${documentId}/analysis/reject`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ reason }),
  });

  const payload: ApiResponse<never> = (await safeJson<ApiResponse<never>>(response)) ?? { ok: false };

  if (!response.ok || !payload.ok || !payload.analysis) {
    throw new Error(payload.error ?? `Rejet impossible (${response.status})`);
  }

  return payload.analysis;
}

async function safeJson<T>(response: Response): Promise<T | null> {
  try {
    return (await response.json()) as T;
  } catch {
    return null;
  }
}
